/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/

/** Import ENVIRONMENTAL VARIABLES */
import ENV from "react-dotenv";

/** Import Main React Library Object, with Hooks */
import React, {useContext, useEffect, useState} from "react";

/** Import Redux Hook - Dispatcher for Actions on Global State Store Object */
import {useDispatch} from "react-redux";
/** Import Redux Hook - Getter for Data from Application State Store Object */
import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";
/** Import REST API Request Status Action - Change Global Rest API Request Status */
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";
/** Import User Login Action - Login User in Global State Store */
import {LOG_USER_IN_ACTION} from "@Actions/Authentication";


import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";


/** Import API Context - REST API Endpoints URLS */
import ApplicationApiContext from "@Contexts/Api";


/** Import Helmet Module - Managing Head of the document (like title, etc...) */
import {Helmet} from "react-helmet";


/** Import Router Modules - Application Routing System */
import {Redirect} from "react-router-dom";


/** Import Component Grid Components */
import BlockContentContainerComponent from "@Components/UI/Containers/Block"; // Main Content Block


/** Import Component Children Components */
import LoginFormComponent from "@UIPages/NotAuthenticated/Login/Form";
import ApplicationHeaderComponent from "@UIParts/Header";
import {Col} from "react-bootstrap";
import BlockContentPictureComponent from "@Components/UI/Containers/Block/Picture";
import GET_USER_DATA_API_REQUEST from "@Services/Application/Requests/User/GetData";
import prepareRestApiRequestToken from "@Services/Utilities/RequestToken/Prepare";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";
import HTTP_ERROR_RESPONSE_INTERFACE from "@Services/Interfaces/Http/Response/Error";
import CHANGE_API_REQUEST_TOKEN_ACTION from "@Actions/Api/ChangeApiRequestTokenValue";



/** @interface LoginFormFieldsInterface - Login Form Fields Types */
interface LoginFormFieldsInterface {
    formUserLogin: string; // User Login Value
    formUserPassword: string; // User Password Value
}

/** @const {LoginFormFieldsInterface} LoginFormFieldsDefaults - Login Form Fields Default Values */
const LoginFormFieldsDefaults: LoginFormFieldsInterface = {
    formUserLogin: "",
    formUserPassword: ""
};

/** @interface LoginFormErrorsInterface - Login Form Errors Types */
export interface LoginFormErrorsInterface {
    loginProcess?: string | null; // Process Error - API Response Error
    formUserLogin?: string | null; // Incorrect User Login Value Error
    formUserPassword?: string | null; // Incorrect Password Value Error
}


/** @type LoginPageComponentProps - Props List for Login Page Component */
type LoginPageComponentProps = {};


/**
 * @visibleName User Login Page Component
 *
 * @return JSX.Element
 */
export const LoginPageComponent: React.FC<LoginPageComponentProps> = (): JSX.Element | null => {


    /** @const {UserInterface | null} LOGGED_USER - Application Global State Logged User Object */
    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state):ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /** @const DISPATCHER Global State Store Actions Dispatcher */
    const DISPATCHER = useDispatch();


    /** @const {string} REST_API_URL Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL;

    /* Get Default Values for Login Form Fields */
    const {
        formUserLogin: formUserLoginDefault,
        formUserPassword: formUserPasswordDefault
    } = LoginFormFieldsDefaults;

    /** const {LOGIN_FORM_ERRORS, SET_LOGIN_FORM_ERRORS} - State - Login Page Form Errors Object */
    const [LOGIN_FORM_ERRORS, SET_LOGIN_FORM_ERRORS] = useState<LoginFormErrorsInterface>({});

    /** const {string, function} - State - Login Page Form User Login Value */
    const [formUserLogin, setFormUserLogin] = useState<string>(formUserLoginDefault);
    /** const {string, function} - State - Login Page Form User Password Value */
    const [formUserPassword, setFormUserPassword] = useState<string>(formUserPasswordDefault);

    /* Login Page Form User Login Value Handler */
    const changeFormUserLogin = (e: React.ChangeEvent<HTMLInputElement>): void => {
        /** @const {string} userLogin - User Login Field Input Value */
        const userLogin: string = e.target.value;
        /* Check if userLogin length is Correct */
        if (userLogin.length > 5) SET_LOGIN_FORM_ERRORS({...LOGIN_FORM_ERRORS, formUserLogin: null}); // Remove User Login Field Error (if present)
        setFormUserLogin(userLogin); // Set User Login Field actual State Value
    };

    /* Login Page Form User Password Value Handler */
    const changeFormUserPassword = (e: React.ChangeEvent<HTMLInputElement>): void => {
        /** @const {string} userPassword - User Password Field Input Value */
        const userPassword: string = e.target.value;
        /* Check if userPassword length is Correct */
        if (userPassword.length > 7) SET_LOGIN_FORM_ERRORS({...LOGIN_FORM_ERRORS, formUserPassword: null}); // Remove User Password Field Error (if present)
        setFormUserPassword(userPassword); // Set User Password Field actual State Value
    };


    const [enterKeyPressCount, setEnterKeyPressCount] = useState<number>(0);

    useEffect(() => {
        let n = 0;
        const HANDLER = (e: KeyboardEvent) => {
            if (e.key === "Enter") setEnterKeyPressCount(++n);
        }
        document.addEventListener("keydown", HANDLER);
        return () => document.removeEventListener("keydown", HANDLER);
    }, []);

    useEffect(() => {
        if (enterKeyPressCount === 0) return;
        LOGIN_FORM_SUBMIT_HANDLER();
    }, [enterKeyPressCount]);


    /* Check if User is already Logged */
    if (LOGGED_USER && LOGGED_USER?.userID) return <Redirect to={"/"} />; // User is Logged - Redirect to Home Page


    /**
     * @async LOGIN_FORM_SUBMIT_HANDLER
     *
     * HANDLE User Login Submit
     * CALL User Login Request
     *
     */
    const LOGIN_FORM_SUBMIT_HANDLER = async () => {

        /* Reset Login Form Errors */
        SET_LOGIN_FORM_ERRORS({}); // Remove All Previous Errors

        /** @const {formErrors} - Login Form Submit Errors Object */
        const formErrors: LoginFormErrorsInterface = {};

        /* Validate User Login Field Value - Before Request */
        if (formUserLogin.length < 6) formErrors.formUserLogin = "Podany identyfikator jest zbyt krótki"; // Set Login Field Error Message
        /* Validate User Password Field Value - Before Request */
        if (formUserPassword.length < 6) formErrors.formUserPassword = "Podane hasło jest zbyt krótkie"; // Set Login Field Error Message

        /* Set User Login Form Errors */
        SET_LOGIN_FORM_ERRORS(formErrors);

        /* Check if Login Form Errors Appeared */
        if (Object.entries(formErrors).length) return null; // Stop Form Submit Handling

        /* No Errors - Handle Login Request */
        await LOGIN_FORM_REQUEST_HANDLER();

    };


    const LOGIN_FORM_REQUEST_HANDLER = async () => {

        DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(true));

        const _LOGIN_REQUEST_DATA = {UserLogin: formUserLogin, Password: btoa(formUserPassword)};

        try {

            const LOGIN_RESPONSE = await REST_API_REQUEST(`${REST_API_URL}/User/Login`, _LOGIN_REQUEST_DATA);

            if (LOGIN_RESPONSE.status === 200) {

                const LOGIN_RESPONSE_CONTENT: any = await LOGIN_RESPONSE.json();

                let TOKEN: AUTHORIZATION_TOKEN_INTERFACE | null = prepareRestApiRequestToken(LOGIN_RESPONSE.headers);

                const {UserId: USER_ID} = LOGIN_RESPONSE_CONTENT;

                const USER_RESPONSE = await GET_USER_DATA_API_REQUEST(USER_ID, `${REST_API_URL}/User/Get`, TOKEN);

                if (USER_RESPONSE?.USER) {

                    DISPATCHER(CHANGE_API_REQUEST_TOKEN_ACTION(USER_RESPONSE.TOKEN));

                    const USER = USER_RESPONSE?.USER?.Driver;

                    const userLogin = USER ? USER_RESPONSE.USER.Login : "";
                    const userFirstName = USER ? USER[0]?.Firstname : "";
                    const userLastName = USER ? USER[0]?.Surname : "";

                    DISPATCHER(LOG_USER_IN_ACTION({
                        userID: USER_ID, userLogin,
                        userFirstName: userFirstName || "",
                        userLastName: userLastName || "",
                        userLoginTime: new Date(), userLastActivityTime: new Date()
                    }));

                } else {
                    SET_LOGIN_FORM_ERRORS({...LOGIN_FORM_ERRORS, loginProcess: USER_RESPONSE.ERROR?.MESSAGE});
                }

            } else {
                const ERROR_RESPONSE_CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await LOGIN_RESPONSE.json();
                SET_LOGIN_FORM_ERRORS({...LOGIN_FORM_ERRORS, loginProcess: ERROR_RESPONSE_CONTENT.UserMessage});
            }

        } catch (e) {
            SET_LOGIN_FORM_ERRORS({...LOGIN_FORM_ERRORS, loginProcess: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
        }

        DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(false));

    };


    return (
        <BlockContentContainerComponent as={"main"} cssClasses={"login-page-component"}>

            <Helmet>
                <title>{`${ENV?.APP_TITLE || "eKiosk"} - Logowanie`}</title>
            </Helmet>

            <ApplicationHeaderComponent />

            <Col xs={12} as={"h2"} className={"login-page-header user-form-header"}>
                <span>{`eKiosk - Aplikacja dla Kierowcy`}</span>
            </Col>

            <BlockContentPictureComponent headerPictureType={2} />

            <LoginFormComponent
                loginFormErrors={LOGIN_FORM_ERRORS}
                formUserLogin={formUserLogin}
                formUserLoginHandler={changeFormUserLogin}
                formUserPassword={formUserPassword}
                formUserPasswordHandler={changeFormUserPassword}
                loginFormSubmitHandler={LOGIN_FORM_SUBMIT_HANDLER}
            />

        </BlockContentContainerComponent>
    )

};


export default LoginPageComponent;
